import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Menu, Plus, X, Settings as SettingsIcon, LogOut, Coins, Bot, Workflow as WorkflowIcon } from "lucide-react";
import Sidebar, { ConversationsSkeleton, SidebarTabs } from "../components/Sidebar";
import NotesPanel from "../components/NotesPanel";
import Logo from "../components/Logo";
import ChatInput from "../components/ChatInput";
import AgentSelector from "../components/AgentSelector";
import MessageBubble, { TypingIndicator } from "../components/MessageBubble";
import AgentTimeline from "../components/AgentTimeline";
import ConfirmDialog from "../components/ConfirmDialog";
import PlansDialog from "../components/PlansDialog";
import InstallAppBanner from "../components/InstallAppBanner";
import { app as appApi, isTaskFinished } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useHiddenTasks } from "../lib/hidden-tasks";
import { useTasks, taskContentCache } from "../lib/tasks-context";
import { useAgents } from "../lib/agents-context";

const SUGGESTIONS = [
    {
        title: "Plano de marketing 30 dias",
        subtitle: "estratégia pronta pra crescer no Instagram",
        prompt: "Monte um plano de marketing de 30 dias para crescer minha conta no Instagram. Inclua pilares de conteúdo, frequência de posts, ideias de Reels, métricas e um cronograma semanal.",
    },
    {
        title: "Decifre uma planilha",
        subtitle: "anexe um CSV/Excel e eu interpreto os dados",
        prompt: "Vou anexar uma planilha. Identifique padrões, anomalias e gere 3 insights acionáveis. No final, sugira 2 gráficos que ajudariam a contar a história dos dados.",
    },
    {
        title: "Tendências de IA em 2026",
        subtitle: "o que muda em modelos generativos este ano",
        prompt: "Me dê um panorama das principais tendências em IA generativa para 2026 (modelos, agentes, multimodalidade, regulação). Resuma em tópicos e cite implicações práticas para quem trabalha com produto.",
    },
    {
        title: "Gere uma imagem",
        subtitle: "descreva a cena e eu crio em segundos",
        prompt: "Gere uma imagem em estilo cinematográfico de um café aconchegante em Paris ao entardecer, vista da rua, luz dourada, pessoas conversando ao fundo, lente 35mm, atmosfera nostálgica.",
    },
];

/**
 * Card de sugestão com efeito 3D (tilt seguindo o mouse + glow radial + pressed scale).
 * Performance: usa CSS custom props via DOM ref — zero re-render durante mousemove.
 */
function Suggestion3DCard({ title, subtitle, onClick, testId }) {
    const cardRef = React.useRef(null);

    const handleMove = (e) => {
        const card = cardRef.current;
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const rotY = ((x - cx) / cx) * 7;   // até 7° horizontal
        const rotX = -((y - cy) / cy) * 7;  // até 7° vertical
        card.style.setProperty("--rx", `${rotX}deg`);
        card.style.setProperty("--ry", `${rotY}deg`);
        card.style.setProperty("--mx", `${(x / rect.width) * 100}%`);
        card.style.setProperty("--my", `${(y / rect.height) * 100}%`);
    };

    const handleLeave = () => {
        const card = cardRef.current;
        if (!card) return;
        card.style.setProperty("--rx", "0deg");
        card.style.setProperty("--ry", "0deg");
        card.style.setProperty("--mx", "50%");
        card.style.setProperty("--my", "50%");
    };

    return (
        <button
            ref={cardRef}
            type="button"
            onClick={onClick}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            data-testid={testId}
            className="suggestion-card-3d group relative text-left rounded-2xl border border-border bg-card overflow-hidden"
            style={{
                "--rx": "0deg",
                "--ry": "0deg",
                "--mx": "50%",
                "--my": "50%",
            }}
        >
            {/* Glow radial que segue o mouse */}
            <div
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    background:
                        "radial-gradient(360px circle at var(--mx) var(--my), rgba(34,197,94,0.18), transparent 45%)",
                }}
            />
            {/* Brilho na borda seguindo o mouse */}
            <div
                className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    background:
                        "radial-gradient(220px circle at var(--mx) var(--my), rgba(34,197,94,0.55), transparent 70%)",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "1px",
                }}
            />
            {/* Conteúdo (com leve translateZ pra reforçar 3D) */}
            <div
                className="relative p-4 sm:p-5 z-10"
                style={{ transform: "translateZ(24px)" }}
            >
                <div className="text-sm sm:text-[15px] font-semibold mb-1 group-hover:text-primary transition-colors leading-tight">
                    {title}
                </div>
                <div className="text-xs text-muted-foreground leading-snug line-clamp-2">
                    {subtitle}
                </div>
            </div>
        </button>
    );
}

export default function ChatPage() {
    const { taskId: taskIdParam } = useParams();
    const navigate = useNavigate();
    const { user, tokensInfo, setTokensInfo, logout } = useAuth();
    const { filter: filterHidden, hide: hideTask } = useHiddenTasks();
    const { tasksRaw, loading: tasksLoading, loadTasks, removeTask } = useTasks();
    const { selectedAgentId, agents, openAgentsDialog } = useAgents();
    const tasks = React.useMemo(() => filterHidden(tasksRaw), [tasksRaw, filterHidden]);
    const [currentTaskId, setCurrentTaskId] = useState(taskIdParam || null);
    const [messages, setMessages] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [durationMs, setDurationMs] = useState(null);
    const [sending, setSending] = useState(false);
    const [waitingAI, setWaitingAI] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState("chats"); // "chats" | "notes"
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    // Map taskId -> { timer, sentAt, finished }. Pollers continuam rodando
    // em background mesmo quando o usuário muda de conversa, de forma que
    // ao retornar à conversa o cache já esteja com a última resposta da IA
    // (zero "atraso" perceptível ao alternar entre chats).
    const pollersRef = useRef(new Map());
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    // Marca quando uma nova mensagem foi enviada — usado para ignorar
    // respostas "stale" do polling que ainda refletem a execução anterior.
    const executionStartedRef = useRef(0);
    // Ref espelho do currentTaskId, usado pelos pollers em background para
    // saber se devem atualizar o React state ou só o cache silenciosamente.
    const currentTaskIdRef = useRef(taskIdParam || null);
    // Epoch da "sessão de chat ativa". Sempre que o usuário inicia uma nova
    // conversa ou seleciona outra, este número é incrementado. Operações
    // assíncronas (criar tarefa, polling, status, etc) capturam o epoch
    // no início e devem descartar o resultado se o epoch já mudou — assim
    // a UI nunca é "puxada de volta" para uma conversa que o usuário já abandonou.
    const chatEpochRef = useRef(0);
    // Cache local de mensagens/timeline por taskId — agora apontamos para
    // o cache global em escopo de módulo (sobrevive a desmontagens da
    // ChatPage quando o usuário vai pra Configurações e volta).
    const taskCacheRef = useRef(taskContentCache);
    // Quando handleSend cria uma tarefa nova e seta o currentTaskId, queremos
    // pular UMA execução do effect de "carregar conversa" — pois o handleSend
    // já deixou as mensagens otimistas no estado e já iniciou o polling.
    // Sem isso, o effect limpa a tela por alguns segundos até o status() voltar.
    const suppressLoadRef = useRef(null);

    // O contexto TasksProvider já carrega na inicialização.
    useEffect(() => { setCurrentTaskId(taskIdParam || null); }, [taskIdParam]);
    useEffect(() => { currentTaskIdRef.current = currentTaskId; }, [currentTaskId]);

    useEffect(() => {
        if (!currentTaskId) {
            setMessages([]); setTimeline([]); setDurationMs(null); setWaitingAI(false); return;
        }
        // Pula uma rodada se acabamos de criar essa task pelo handleSend —
        // ele já cuidou de tudo (mensagens otimistas + poller iniciado).
        if (suppressLoadRef.current === currentTaskId) {
            suppressLoadRef.current = null;
            return;
        }
        // 1. Render INSTANTÂNEO a partir do cache local
        const cached = taskCacheRef.current.get(currentTaskId);
        if (cached) {
            setMessages(cached.messages || []);
            setTimeline(cached.timeline || []);
            setDurationMs(cached.durationMs ?? null);
            preloadAttachments(cached.messages || []);
        } else {
            const t = tasks.find((x) => x.id === currentTaskId);
            if (t?.messages) {
                const m = normalizeMessages(t.messages);
                setMessages(m);
                taskCacheRef.current.set(currentTaskId, { messages: m, timeline: [], durationMs: null });
            } else {
                setMessages([]);
                setTimeline([]);
                setDurationMs(null);
            }
        }

        // Se já existe um poller em background para essa conversa, a UI
        // entra em "aguardando IA" imediatamente — sem esperar o próximo
        // status() retornar.
        const existing = pollersRef.current.get(currentTaskId);
        if (existing && !existing.finished) {
            setWaitingAI(true);
            return;
        }

        // Se o cache mostra um timeline em execução (alguma linha
        // "doing"), assumimos que a tarefa ainda pode estar rodando —
        // mantemos a animação ligada até o status() voltar. Isso evita
        // o "flicker": entrar na conversa, ver tudo parado por 1s,
        // depois a animação voltar.
        const cachedTimeline = (cached?.timeline) || [];
        const looksRunning = cachedTimeline.some((ln) => ln?.status === "doing");
        setWaitingAI(looksRunning);

        // 2. Refresh em background — captura epoch, descarta se mudou
        const epoch = chatEpochRef.current;
        const taskId = currentTaskId;
        (async () => {
            try {
                const st = await appApi.status(taskId);
                if (epoch !== chatEpochRef.current) return; // usuário trocou de conversa
                const fresh = normalizeMessages(st.messages_local || []);
                preloadAttachments(fresh);
                const isRunning = st.progress === "running" && !isTaskFinished(st);
                const tl = isRunning ? (st.timeline || []) : [];
                const dur = st.duration_ms ?? null;
                taskCacheRef.current.set(taskId, { messages: fresh, timeline: tl, durationMs: dur });
                setMessages(fresh);
                setTimeline(tl);
                setDurationMs(dur);
                if (isRunning) {
                    setWaitingAI(true);
                    ensurePoller(taskId, 0);
                } else {
                    // só desliga a animação se realmente terminou
                    setWaitingAI(false);
                }
            } catch { /* ignora */ }
        })();
        // eslint-disable-next-line
    }, [currentTaskId]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Scroll automático pro fim apenas se o usuário já estava próximo do fim
        // (evita "puxar" a tela enquanto ele está lendo mensagens anteriores).
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < 250) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, waitingAI, timeline.length]);

    useEffect(() => () => stopAllPollers(), []);

    function stopPollerFor(taskId) {
        const p = pollersRef.current.get(taskId);
        if (!p) return;
        p.finished = true;
        if (p.timer) clearTimeout(p.timer);
        pollersRef.current.delete(taskId);
    }

    function stopAllPollers() {
        for (const [, p] of pollersRef.current) {
            p.finished = true;
            if (p.timer) clearTimeout(p.timer);
        }
        pollersRef.current.clear();
    }

    /**
     * Garante que existe um poller rodando em background para `taskId`.
     * Pollers continuam vivos mesmo quando o usuário navega para outra
     * conversa — eles atualizam silenciosamente o cache. Assim, quando o
     * usuário voltar à conversa, a UI já reflete a última resposta da IA
     * (sem esperar uma nova chamada de /status).
     *
     * Se `sentAt > 0`, o poller usa esse timestamp para considerar a
     * resposta atual da execução (vide started_at do servidor).
     */
    function ensurePoller(taskId, sentAt = 0) {
        if (!taskId) return;
        const existing = pollersRef.current.get(taskId);
        if (existing && !existing.finished) {
            if (sentAt > existing.sentAt) existing.sentAt = sentAt;
            return;
        }
        const controller = {
            timer: null,
            sentAt,
            finished: false,
            // Quantas vezes o backend já disse "done" sem a mensagem da
            // IA ter chegado. Serve para fazer retry rápido nesse caso
            // raro e nunca deixar a UI travada "sem nada".
            doneWithoutReplyAttempts: 0,
        };
        pollersRef.current.set(taskId, controller);

        const tick = async () => {
            if (controller.finished) return;
            if (pollersRef.current.get(taskId) !== controller) return;
            try {
                const st = await appApi.status(taskId);
                if (controller.finished) return;

                const serverStarted = st.started_at ? new Date(st.started_at).getTime() : 0;
                const isCurrent = controller.sentAt === 0 || serverStarted >= controller.sentAt - 1500;

                const normalized = normalizeMessages(st.messages_local || []);
                preloadAttachments(normalized);
                const tlNow = isCurrent ? (st.timeline || []) : [];
                const durNow = st.duration_ms ?? null;

                // SEMPRE atualiza o cache, mesmo que o usuário não esteja
                // olhando essa conversa — isso é o que elimina o atraso ao
                // voltar pra ela.
                taskCacheRef.current.set(taskId, {
                    messages: normalized,
                    timeline: tlNow,
                    durationMs: durNow,
                });

                // UI: só atualiza se o usuário está vendo essa conversa
                if (currentTaskIdRef.current === taskId) {
                    setMessages(normalized);
                    if (isCurrent) {
                        setTimeline(tlNow);
                        setDurationMs(durNow);
                    }
                }

                const serverFinished = st.finished_at ? new Date(st.finished_at).getTime() : 0;
                const finishSignal = isCurrent && (
                    st.progress === "done" ||
                    (serverFinished > 0 && (controller.sentAt === 0 || serverFinished >= controller.sentAt)) ||
                    isTaskFinished(st)
                );

                // GUARDA CRÍTICA: o backend pode marcar progress=done UM
                // tick antes da mensagem do assistente aparecer em
                // messages_local. Se a gente parasse o poller aqui, a UI
                // ficaria com a mensagem do usuário, SEM animação, SEM
                // resposta. Por isso só consideramos a tarefa realmente
                // terminada quando há uma mensagem do assistente após a
                // última do usuário.
                //
                // Em casos extremos onde isso nunca acontece (ex.: tarefa
                // cancelada sem resposta), liberamos depois de ~6s para
                // o usuário não ficar preso pra sempre.
                const replyPresent = hasAssistantReplyAfterUser(normalized);

                if (finishSignal && !replyPresent) {
                    controller.doneWithoutReplyAttempts += 1;
                    // mantém waitingAI=true (não toca) para a animação
                    // continuar até a mensagem chegar
                    if (currentTaskIdRef.current === taskId) {
                        setWaitingAI(true);
                    }
                    // até 20 tentativas a 300ms = 6s antes de aceitar o fim
                    if (controller.doneWithoutReplyAttempts < 20) {
                        if (pollersRef.current.get(taskId) === controller) {
                            controller.timer = setTimeout(tick, 300);
                        }
                        return;
                    }
                    // após o limite: aceita o fim mesmo sem resposta
                }

                if (finishSignal) {
                    controller.finished = true;
                    pollersRef.current.delete(taskId);
                    if (currentTaskIdRef.current === taskId) {
                        // Só apaga a animação DEPOIS que a mensagem já
                        // foi colocada no state (setMessages acima).
                        setWaitingAI(false);
                    }
                    loadTasks();
                    appApi.tokens().then((tok) => tok && setTokensInfo(tok)).catch(() => {});
                    return;
                }
            } catch {
                // Mantém o polling vivo — erros transitórios de rede não devem matar o stream
            }
            if (!controller.finished && pollersRef.current.get(taskId) === controller) {
                // Polling mais rápido se o usuário está vendo a conversa,
                // mais lento se está em background (economia de rede).
                const delay = currentTaskIdRef.current === taskId ? 700 : 1500;
                controller.timer = setTimeout(tick, delay);
            }
        };
        controller.timer = setTimeout(tick, 100);
    }

    const handleNewChat = () => {
        chatEpochRef.current += 1;
        executionStartedRef.current = 0;
        // NÃO paramos pollers em background — conversas que ainda estão
        // gerando resposta continuam rodando silenciosamente, então ao
        // voltar pra elas a UI já aparece pronta.
        setWaitingAI(false);
        setSending(false);
        setMessages([]);
        setTimeline([]);
        setDurationMs(null);
        inputRef.current?.clear();
        setCurrentTaskId(null);
        navigate("/chat");
        setMobileDrawerOpen(false);
    };

    const handleSelectTask = (task) => {
        chatEpochRef.current += 1;
        executionStartedRef.current = 0;
        // Mantém pollers em background ativos — o estado é restaurado
        // a partir do cache no useEffect de currentTaskId.
        setSending(false);
        setTimeline([]);
        setDurationMs(null);
        inputRef.current?.clear();
        setCurrentTaskId(task.id);
        navigate(`/chat/${task.id}`);
        setMobileDrawerOpen(false);
    };

    const handleSend = async ({ text, files, multi = 1 }) => {
        if (sending || waitingAI) return;
        const taskCost = tokensInfo?.task_cost ?? 50;
        const totalCost = taskCost * multi;
        if (!tokensInfo?.unlimited && (tokensInfo?.tokens ?? 0) < totalCost) {
            if (multi > 1) {
                const maxPossible = Math.max(Math.floor((tokensInfo?.tokens ?? 0) / taskCost), 0);
                toast.error(`Saldo insuficiente para ${multi}x (${totalCost.toLocaleString("pt-BR")} tokens). Seu saldo permite no máximo ${maxPossible}x.`);
            } else {
                toast.error("Saldo de tokens insuficiente. Adquira mais créditos.");
            }
            return;
        }
        // Captura o epoch da conversa atual. Se o usuário mudar de chat
        // antes do await retornar, descartamos o resultado.
        const epoch = chatEpochRef.current;
        // Disparo múltiplo cria SEMPRE conversas novas (nunca continua a atual)
        const taskIdAtSend = multi > 1 ? null : currentTaskId;
        // Marca o instante do envio (usado para invalidar polling da execução anterior)
        executionStartedRef.current = Date.now();
        // Limpa timeline da execução anterior antes de começar a nova
        setTimeline([]);
        setDurationMs(null);
        const optimisticMsg = {
            role: "user",
            content: text || "",
            attachments: files.map((f) => ({
                filename: f.filename, url: f.url, content_type: f.content_type,
                type: f.content_type?.startsWith("image/") ? "image" : "file",
            })),
        };
        setMessages((prev) => (multi > 1 ? [optimisticMsg] : [...prev, optimisticMsg]));
        setSending(true);
        setWaitingAI(true);
        try {
            const agentIdToUse = !taskIdAtSend ? selectedAgentId : null;
            const body = buildCreateBody({ text, files, taskId: taskIdAtSend, agentId: agentIdToUse });
            const res = await appApi.create(body);
            // Se o usuário trocou de conversa enquanto a requisição estava
            // em vôo, ignoramos completamente o resultado.
            if (epoch !== chatEpochRef.current) {
                loadTasks();
                return;
            }
            const newTaskId = res.task_id;
            if (!taskIdAtSend) {
                // Já temos a mensagem otimista na tela — popula o cache COM ela
                // e marca o effect pra não rodar (senão ele limparia a tela).
                suppressLoadRef.current = newTaskId;
                taskCacheRef.current.set(newTaskId, {
                    messages: [optimisticMsg],
                    timeline: [],
                    durationMs: null,
                });
                setCurrentTaskId(newTaskId);
                navigate(`/chat/${newTaskId}`, { replace: true });
            }
            if (typeof res.tokens_remaining === "number") {
                setTokensInfo((prev) => prev
                    ? { ...prev, tokens: res.tokens_remaining }
                    : { tokens: res.tokens_remaining, task_cost: 50, unlimited: false });
            }
            ensurePoller(newTaskId, executionStartedRef.current);
            loadTasks();
            // Disparo múltiplo: cria as demais conversas em background
            if (multi > 1) {
                spawnExtraConversations(buildCreateBody({ text, files, taskId: null, agentId: selectedAgentId }), multi - 1, multi);
            }
        } catch (err) {
            // Se já saímos dessa conversa, ignora o erro silenciosamente
            if (epoch !== chatEpochRef.current) return;
            const detail = err?.response?.data?.detail || "Erro ao enviar mensagem.";
            toast.error(detail);
            setMessages((prev) => prev.slice(0, -1));
            setWaitingAI(false);
        } finally {
            if (epoch === chatEpochRef.current) setSending(false);
        }
    };

    /**
     * Disparo múltiplo: cria `count` conversas extras com o mesmo pedido,
     * em paralelo (4 por vez), silenciosamente em background.
     */
    async function spawnExtraConversations(baseBody, count, total) {
        let failures = 0;
        let idx = 0;
        const worker = async () => {
            while (idx < count) {
                idx += 1;
                try {
                    await appApi.create({ ...baseBody });
                } catch {
                    failures += 1;
                }
                loadTasks();
            }
        };
        await Promise.all(Array.from({ length: Math.min(4, count) }, worker));
        if (failures > 0) {
            toast.warning(`${total - failures} de ${total} conversas criadas (${failures} falharam).`);
        }
        appApi.tokens().then((tok) => tok && setTokensInfo(tok)).catch(() => {});
        loadTasks();
    }

    const fillSuggestion = (s) => {
        // Aceita tanto { prompt } (novo formato) quanto { title, subtitle } (legado)
        const text = s?.prompt || `${s?.title || ""} ${s?.subtitle || ""}`.trim();
        inputRef.current?.setText(text);
        setTimeout(() => inputRef.current?.focus(), 30);
    };

    const doLogout = () => {
        logout();
        navigate("/login");
    };

    const requestDeleteTask = (task) => {
        setTaskToDelete(task);
    };

    const confirmDeleteTask = () => {
        if (!taskToDelete) return;
        const id = taskToDelete.id;
        hideTask(id);
        removeTask(id);
        stopPollerFor(id);
        setTaskToDelete(null);
        // Se estava vendo essa conversa, sai dela
        if (currentTaskId === id) {
            setWaitingAI(false);
            setMessages([]);
            setTimeline([]);
            setDurationMs(null);
            setCurrentTaskId(null);
            navigate("/chat", { replace: true });
        }
        toast.success("Conversa excluída.");
    };

    const showEmpty = !currentTaskId && messages.length === 0 && !waitingAI;

    return (
        <div className="flex h-[100dvh] overflow-hidden bg-background text-foreground">
            <Sidebar
                onNewChat={handleNewChat}
                tasks={tasks}
                loading={tasksLoading}
                currentTaskId={currentTaskId}
                onSelect={handleSelectTask}
                onLogoutRequest={() => setLogoutConfirmOpen(true)}
                onDelete={requestDeleteTask}
            />

            {/* Drawer mobile com slide-in da esquerda */}
            <div
                className={`md:hidden fixed inset-0 z-50 ${mobileDrawerOpen ? "" : "pointer-events-none"}`}
                data-testid="mobile-drawer-wrapper"
            >
                <div
                    className={`absolute inset-0 bg-black/75 transition-opacity duration-300 ${mobileDrawerOpen ? "opacity-100" : "opacity-0"}`}
                    onClick={() => setMobileDrawerOpen(false)}
                />
                <div
                    className={`relative w-[85%] max-w-[320px] h-full bg-[#0d0d0d] border-r border-border flex flex-col transform transition-transform duration-300 ease-out ${mobileDrawerOpen ? "translate-x-0" : "-translate-x-full"}`}
                    data-testid="mobile-drawer"
                >
                    <div className="flex items-center justify-between p-4 border-b border-border">
                        <div className="flex items-center gap-2">
                            <Logo className="w-6 h-6" />
                            <span className="font-display font-bold text-sm tracking-tighter">LINKIA</span>
                        </div>
                        <button onClick={() => setMobileDrawerOpen(false)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors" data-testid="close-drawer" aria-label="Fechar menu">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-4 border-b border-border">
                        <div className="flex gap-2">
                            <button
                                onClick={handleNewChat}
                                data-testid="mobile-new-chat-btn"
                                className="btn-primary flex-1 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5"
                            >
                                <Plus className="w-4 h-4" strokeWidth={2.5} />
                                Nova Conversa
                            </button>
                            <button
                                data-testid="mobile-create-agent-btn"
                                onClick={() => { setMobileDrawerOpen(false); openAgentsDialog("form"); }}
                                title="Criar agente"
                                aria-label="Criar agente"
                                className="shrink-0 w-10 h-10 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors flex items-center justify-center"
                            >
                                <Bot className="w-4 h-4" strokeWidth={2.2} />
                            </button>
                        </div>
                        <button
                            data-testid="mobile-my-agents-btn"
                            onClick={() => { setMobileDrawerOpen(false); openAgentsDialog("list"); }}
                            className="mt-2 w-full py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 bg-[#1d1d1d] border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                        >
                            <Bot className="w-3.5 h-3.5" strokeWidth={2.2} />
                            Meus Agentes{agents.length > 0 ? ` (${agents.length})` : ""}
                        </button>
                        <button
                            data-testid="mobile-bots-pipeline-btn"
                            onClick={() => { setMobileDrawerOpen(false); navigate("/bots"); }}
                            className="mt-2 w-full py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 bg-[#1d1d1d] border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                        >
                            <WorkflowIcon className="w-3.5 h-3.5" strokeWidth={2.2} />
                            Pipeline de Bots
                        </button>
                    </div>
                    <SidebarTabs tab={drawerTab} onChange={setDrawerTab} />
                    <div className="flex-1 overflow-y-auto px-2 py-2">
                        {drawerTab === "notes" ? (
                            <NotesPanel onItemOpen={() => setMobileDrawerOpen(false)} />
                        ) : tasksLoading && tasks.length === 0 ? (
                            <ConversationsSkeleton count={7} />
                        ) : (
                        <div className="flex flex-col gap-1">
                            {tasks.slice(0, 50).map((t) => (
                                <div
                                    key={t.id}
                                    className={`group relative flex items-center w-full rounded-sm ${currentTaskId === t.id ? "bg-muted text-foreground border-l-2 border-primary" : "text-muted-foreground hover:bg-muted/60 active:bg-muted"}`}
                                >
                                    <button
                                        onClick={() => handleSelectTask(t)}
                                        className="flex-1 min-w-0 text-left px-3 py-2 text-sm truncate"
                                        data-testid={`drawer-task-${t.id}`}
                                    >
                                        {t.title || "Conversa"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); requestDeleteTask(t); setMobileDrawerOpen(false); }}
                                        aria-label="Excluir conversa"
                                        data-testid={`drawer-task-delete-${t.id}`}
                                        className="p-2 mr-1 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </button>
                                </div>
                            ))}
                            {tasks.length === 0 && <div className="text-xs text-muted-foreground px-3 py-4">Sem conversas ainda.</div>}
                        </div>
                        )}
                    </div>
                    <div className="border-t border-border p-4">
                        <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{user?.name || "Usuário"}</div>
                                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                            </div>
                            <button
                                onClick={() => { navigate("/configuracoes"); setMobileDrawerOpen(false); }}
                                className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                aria-label="Configurações"
                                data-testid="drawer-settings"
                            >
                                <SettingsIcon className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => { setMobileDrawerOpen(false); setLogoutConfirmOpen(true); }}
                                className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                aria-label="Sair"
                                data-testid="drawer-logout"
                            >
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main: layout flex column — header e input fixos, messages rolam */}
            <main className="flex-1 flex flex-col min-w-0 h-[100dvh]">
                {/* Header fixo */}
                <header className="shrink-0 h-12 md:h-14 border-b border-border bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center justify-between px-3 sm:px-6" data-testid="chat-header">
                    <button
                        className="md:hidden p-2 -ml-2 text-muted-foreground active:text-primary transition-colors"
                        onClick={() => setMobileDrawerOpen(true)}
                        data-testid="open-drawer-btn"
                        aria-label="Abrir menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    {/* Agente: controles completos sempre visíveis */}
                    <AgentSelector />
                    <div className="flex-1" />
                    <div className="flex items-center gap-2">
                        <div
                            className="flex items-center gap-1.5 bg-muted/40 border border-border rounded-full px-3 py-1"
                            data-testid="token-balance"
                        >
                            <span className="font-mono text-[11px] md:text-xs font-semibold text-foreground">
                                {tokensInfo?.unlimited ? "∞" : (tokensInfo?.tokens ?? user?.tokens ?? 0).toLocaleString("pt-BR")}
                            </span>
                            <span className="text-[9px] md:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">tokens</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setPlansOpen(true)}
                            data-testid="recharge-btn"
                            className="flex items-center gap-1.5 bg-primary hover:opacity-90 text-white rounded-full px-3 py-1 transition-opacity"
                            aria-label="Recarregar tokens"
                        >
                            <Coins className="w-3.5 h-3.5" strokeWidth={2.2} />
                            <span className="hidden md:inline text-[11px] md:text-xs font-semibold">Recarregar</span>
                        </button>
                    </div>
                </header>

                {/* Área de mensagens — única parte que rola */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto"
                    data-testid="chat-messages"
                >
                    {showEmpty ? (
                        <div className="min-h-full flex flex-col items-center justify-center px-4 sm:px-6 py-8" data-testid="chat-empty-state">
                            <div className="mb-4 relative">
                                <div className="absolute inset-0 blur-3xl opacity-40" style={{ background: "radial-gradient(circle, #22C55E, transparent)" }} />
                                <Logo className="w-20 h-20 relative" />
                            </div>
                            <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-center mb-1.5">
                                No que posso ajudar?
                            </h1>
                            <p className="text-xs sm:text-sm text-muted-foreground text-center max-w-md mb-6 sm:mb-8 px-4">
                                Pergunte qualquer coisa, anexe arquivos, peça uma análise.
                            </p>
                            <div className="grid grid-cols-2 gap-3 w-full max-w-xl perspective-1000">
                                {SUGGESTIONS.map((s, i) => (
                                    <Suggestion3DCard
                                        key={i}
                                        title={s.title}
                                        subtitle={s.subtitle}
                                        onClick={() => fillSuggestion(s)}
                                        testId={`suggestion-${i}`}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-3xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-8 space-y-5 sm:space-y-6">
                            {messages.map((m, i) => {
                                const isLastUser =
                                    m.role === "user" &&
                                    !messages.slice(i + 1).some((x) => x.role === "user");
                                return (
                                    <React.Fragment key={i}>
                                        <MessageBubble msg={m} />
                                        {isLastUser && timeline.length > 0 && waitingAI && (
                                            <AgentTimeline timeline={timeline} running={waitingAI} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {waitingAI && timeline.length === 0 && <TypingIndicator />}
                        </div>
                    )}
                </div>

                {/* Input fixo */}
                <div className="shrink-0 border-t border-border bg-background px-3 sm:px-6 pt-3 pb-3 md:pb-4" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
                    <div className="max-w-3xl mx-auto w-full">
                        <ChatInput
                            ref={inputRef}
                            onSend={handleSend}
                            disabled={sending || waitingAI}
                        />
                    </div>
                </div>
            </main>

            <ConfirmDialog
                open={logoutConfirmOpen}
                title="Sair da conta?"
                description="Você precisará fazer login novamente para acessar suas conversas."
                confirmLabel="Sim, sair"
                cancelLabel="Cancelar"
                onCancel={() => setLogoutConfirmOpen(false)}
                onConfirm={doLogout}
                destructive
                testId="logout-confirm"
            />

            <ConfirmDialog
                open={!!taskToDelete}
                title="Excluir conversa?"
                description={
                    taskToDelete
                        ? `"${taskToDelete.title || "Conversa sem título"}" será removida da sua lista. Esta ação não pode ser desfeita.`
                        : ""
                }
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                onCancel={() => setTaskToDelete(null)}
                onConfirm={confirmDeleteTask}
                destructive
                testId="delete-task-confirm"
            />

            <PlansDialog open={plansOpen} onClose={() => setPlansOpen(false)} />
            <InstallAppBanner />
        </div>
    );
}

// Cache global de URLs já pré-carregadas (evita refazer Image() de novo)
const _preloadedUrls = new Set();
function preloadAttachments(messages) {
    if (typeof window === "undefined") return;
    for (const m of messages || []) {
        for (const a of m.attachments || []) {
            const url = a?.url;
            const ct = a?.content_type || "";
            if (!url || _preloadedUrls.has(url)) continue;
            if (ct.startsWith("image/") || a?.type === "image") {
                _preloadedUrls.add(url);
                try {
                    const img = new Image();
                    img.decoding = "async";
                    img.src = url;
                } catch {
                    /* noop */
                }
            }
        }
    }
}


function normalizeMessages(msgs) {
    const normalized = (msgs || []).map((m) => {
        let content = typeof m.content === "string" ? m.content : (m.content?.[0]?.text || "");
        // Remove marcadores internos "[file: nome.ext]" que a API inclui
        // no conteúdo do usuário quando há anexos. Os anexos já são
        // renderizados como bolha de mídia abaixo, não precisam virar texto.
        content = content
            .replace(/\[file:\s*[^\]]+\]/gi, "")
            .replace(/\[image:\s*[^\]]+\]/gi, "")
            .replace(/\[audio:\s*[^\]]+\]/gi, "")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
        return {
            role: m.role || "assistant",
            content,
            attachments: m.attachments || [],
            timestamp: m.timestamp,
        };
    });
    // Colapsa mensagens consecutivas do assistente — a API externa persiste
    // preâmbulos/pensamentos intermediários ("Com certeza! Vou gerar...",
    // "Estou gerando uma imagem...", etc.) antes da resposta final. Para
    // cada bloco consecutivo de mensagens do assistente, mantém apenas a
    // última (que contém a resposta definitiva e anexos).
    const out = [];
    for (const m of normalized) {
        const prev = out[out.length - 1];
        if (m.role === "assistant" && prev && prev.role === "assistant") {
            out[out.length - 1] = m;
        } else {
            out.push(m);
        }
    }
    return out;
}

/**
 * Retorna true se já existe uma mensagem do assistente APÓS a última
 * mensagem do usuário. Serve para o poller decidir que a resposta da
 * IA já chegou — antes disso, a animação "Pensando…" continua, mesmo
 * que o backend já tenha sinalizado `progress=done`.
 */
function hasAssistantReplyAfterUser(msgs) {
    if (!Array.isArray(msgs) || msgs.length === 0) return false;
    let lastUserIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === "user") { lastUserIdx = i; break; }
    }
    if (lastUserIdx === -1) {
        return msgs.some((m) => m.role === "assistant" && m.content);
    }
    return msgs.slice(lastUserIdx + 1).some((m) => m.role === "assistant" && (m.content || (m.attachments && m.attachments.length)));
}


function buildCreateBody({ text, files, taskId, agentId }) {
    const content = [];
    if (text) content.push({ type: "text", text });
    for (const f of files || []) {
        content.push({
            type: "file",
            file_url: f.url,
            filename: f.filename,
            mime_type: f.content_type,
        });
    }
    const body = {};
    if (taskId) body.task_id = taskId;
    if (agentId) body.agent_id = agentId;
    if (files && files.length > 0) body.message = { content };
    else body.prompt = text;
    return body;
}
